import { Link, useSearchParams } from "react-router-dom";

import { Shell } from "../../app/Shell";
import { useCan } from "../../auth/AuthProvider";
import { CAP } from "../../auth/capabilities";
import { FAMILY_LABELS, isProductFamily } from "../../catalog/taxonomy";
import type { ProductFamily } from "../../catalog/taxonomy";
import { ErrorNotice } from "../../components/ui";
import { ProductFamilyTabs } from "./ProductFamilyTabs";
import { ProductListToolbar } from "./ProductListToolbar";
import { ProductTable } from "./ProductTable";
import { useFamilyCounts, useFamilyProducts } from "./useFamilyProducts";
import "./list.css";

/** The family an administrator lands on. The frame shop is the bulk of the
 *  catalogue, so it is the one that opens; there is deliberately no mixed
 *  "all families" view to fall back to. */
const DEFAULT_FAMILY: ProductFamily = "glasses";

/**
 * Product Management: one screen that lists the shop's products and carries
 * every action an administrator takes on one.
 *
 * It reads the committed admin listing endpoint through the shared API client —
 * there is no second client and no second product type — and it owns none of
 * the workflows it offers. Add opens the committed Add Product flow, Edit opens
 * the committed editor for that product, and Archive opens the committed
 * confirmation that performs the archive lifecycle call. This screen is the way
 * in; the rules stay where they already live.
 *
 * The four states a real list has are all real here: loading, empty, failed
 * with a retry, and populated. None of them is a placeholder, and a failure is
 * never allowed to render as an empty catalog — those look identical to an
 * administrator and mean opposite things.
 */
export function ProductManagementPage() {
  const canWrite = useCan(CAP.productsWrite);
  // Query state lives in the URL so a filtered view can be shared or reloaded.
  const [params, setParams] = useSearchParams();

  const search = params.get("search") ?? "";
  const status = params.get("status") ?? "";
  // The family being managed. A URL naming one that does not exist opens the
  // default rather than an empty screen with a tab nothing has selected.
  const familyParam = params.get("family");
  const family = isProductFamily(familyParam) ? familyParam : DEFAULT_FAMILY;
  const page = Number(params.get("page") ?? "1") || 1;

  // Search and status are handed to the family view, which sends them with
  // every category request it makes — so both are scoped to the section on
  // screen and neither can reach a product filed under another family.
  const filters = { search, status };
  const query = useFamilyProducts(family, page, filters);
  const counts = useFamilyCounts(filters);

  function update(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    // Any filter change restarts paging; page 3 of the old result set is
    // meaningless against a new one.
    if (!("page" in next)) merged.delete("page");
    setParams(merged, { replace: true });
  }

  const data = query.page;
  // The family is not a filter here: it is the section being managed, and its
  // own emptiness is a different sentence from "nothing matched what you
  // typed".
  const filtered = Boolean(search || status);

  return (
    <Shell
      title="Products"
      actions={
        // Creating a product is a catalogue write. A viewer reads this list and
        // is not offered a way in to a screen that would refuse them.
        canWrite ? (
          <Link className="btn btn--primary" to="/products/add">
            Add Product
          </Link>
        ) : null
      }
    >
      <div className="stack">
        {/* Family first, because it is what the rest of the screen is about:
            the toolbar below narrows the family on show, and the table below
            that is the family on show. */}
        <ProductFamilyTabs
          family={family}
          counts={counts}
          // Changing section restarts paging and drops the search with it: a
          // term typed to find a lens is not a question about accessories, and
          // carrying it across would answer a tab change with an empty table.
          onSelect={(next) =>
            update({ family: next, search: "", page: "" })
          }
        />

        <ProductListToolbar
          search={search}
          status={status}
          total={data ? data.total : null}
          onSearch={(value) => update({ search: value })}
          onStatus={(value) => update({ status: value })}
        />

        {/* Retry sits beside the message rather than inside it, so the alert an
            assistive reader announces is the sentence and not the button. */}
        {query.error ? (
          <div className="productList__failure">
            <ErrorNotice error={query.error} />
            <button
              type="button"
              className="btn"
              disabled={query.isFetching}
              onClick={() => query.refetch()}
            >
              {query.isFetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : null}

        <div className="panel">
          {query.isPending ? (
            <div className="empty" role="status">
              Loading products…
            </div>
          ) : query.error ? null : data && data.items.length === 0 ? (
            <div className="empty">
              {filtered
                ? `No ${FAMILY_LABELS[family]} products match these filters.`
                : `No ${FAMILY_LABELS[family]} products yet. Choose Add Product to create the first one.`}
            </div>
          ) : data ? (
            <ProductTable items={data.items} />
          ) : null}
        </div>

        {data && data.total_pages > 1 ? (
          <div className="row">
            <button
              type="button"
              className="btn btn--sm"
              disabled={page <= 1}
              onClick={() => update({ page: String(page - 1) })}
            >
              Previous
            </button>
            <span className="muted num">
              Page {data.page} of {data.total_pages}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              disabled={page >= data.total_pages}
              onClick={() => update({ page: String(page + 1) })}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </Shell>
  );
}
